/**
 * The domain requirement rendered as the dedicated gate PANEL (DC-6), not a
 * checklist row. Its button opens the Plan-C `DomainConnectDrawer` through
 * `openDialog('domainDrawer')`; without a host it navigates to the
 * integrations page (`fallbackRoute`).
 *
 * AM-4 defines two gate-caption states, "fresh" and "pending" (DNS
 * verification in progress). The tier-1 projection this task reads
 * (`ActivationItem`) only carries a boolean `earned` for `domainVerified` —
 * the evaluator collapses `customDomains.status` to earned/not-earned
 * (contracts AM-4, `domainEvaluator.ts`) with no pending signal surfaced.
 * Distinguishing "pending" needs that richer status and is explicitly
 * deferred to the DNS table + manual Verify flow ("drawer coverage lands in
 * Plan C" — Plan A Task 17 qa.md). This slot renders the "fresh" caption
 * for every not-yet-earned state; Plan C's drawer owns the pending detail.
 */
import React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import type { ActivationItem } from '@modules/shared-types';
import { LAUNCH_ACTIVATION_TEST_IDS } from '../testIds';

export interface DomainGateSlotProps {
  item: ActivationItem;
  onAction: (item: ActivationItem) => void;
  onRetry: () => void;
}

export const DomainGateSlot: React.FC<DomainGateSlotProps> = ({ item, onAction, onRetry }) => (
  <Paper
    data-testid={LAUNCH_ACTIVATION_TEST_IDS.domainGate}
    variant="outlined"
    sx={{ p: 2.5, mb: 2, borderColor: item.earned ? 'success.main' : 'warning.main' }}
  >
    <Typography variant="h6" component="h2" gutterBottom>{item.title}</Typography>
    <Typography variant="body2" color="text.secondary" sx={{ mb: item.earned ? 0 : 1.5 }}>{item.detail}</Typography>
    {!item.earned && (
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
        <Typography variant="caption" color="text.secondary">Domain not set up yet</Typography>
        {item.checkFailed ? (
          <Button data-testid={LAUNCH_ACTIVATION_TEST_IDS.itemRetry(item.id)} variant="outlined" size="small" onClick={onRetry}>
            Retry
          </Button>
        ) : (
          <Button data-testid={LAUNCH_ACTIVATION_TEST_IDS.domainGateAction} variant="outlined" size="small" onClick={() => onAction(item)}>
            {item.action?.label ?? 'Connect domain'}
          </Button>
        )}
      </Box>
    )}
  </Paper>
);
